/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, { BORDER_WIDTH } from './WireframeBase';
import tools from '../tools';
import wireframeRadioButton from '../assets/icons/wireframeRadioButton.svg';
import wireframeRadioButtonEmpty from '../assets/icons/wireframeRadioButtonEmpty.svg';
import wireframeRadioButtonFilled from '../assets/icons/wireframeRadioButtonFilled.svg';
import { Rect } from '@lib/canvasx/components/Rect';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { SPACER_2 } from '../utils/style';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import { Label } from '../components/wireframes/Label';

const DEFAULT_SIZE = 24;

class WireframeRadioButton extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);
    // Backwards compat: uiStates used to be default, disabled
    if (!fabricObject.uiState || fabricObject.uiState === 'default') {
      fabricObject.set('uiState', 'enabled');
    }
  }

  tools() {
    return [
      [tools.ToolWireframeComponentState],
      [tools.ToolWireframeLabelPosition],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      this.showLabel ? [tools.ToolWireframeTextColor] : [],
    ];
  }

  static defaultWidth(): number {
    return DEFAULT_SIZE;
  }

  static defaultHeight(): number {
    return DEFAULT_SIZE;
  }

  static iconSvg() {
    return wireframeRadioButton;
  }

  static externalName(): string {
    return 'Radio button';
  }

  static componentStates() {
    return {
      filled: wireframeRadioButtonFilled,
      empty: wireframeRadioButtonEmpty,
    };
  }

  componentStates() {
    return WireframeRadioButton.componentStates();
  }

  get componentStatesTitle() {
    return 'On/off state';
  }

  static labelPositions() {
    return ['left', 'right', 'noLabel'];
  }

  labelPositions(): string[] {
    return WireframeRadioButton.labelPositions();
  }

  get filled() {
    return this.fabricObject.componentState === 'filled';
  }

  get uiStates() {
    return ['enabled', 'disabled'];
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      scaleX,
      componentState = 'filled',
      uiState = 'enabled',
      textColor = 'black',
    } = this.fabricObject;
    const { filled } = this;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const divisor = componentState === 'empty' ? 1.2 : 2;
    const adjustedSize = DEFAULT_SIZE / divisor;
    const opacity = uiState === 'disabled' ? 0.4 : 1;
    let renderedDimensions;

    const x = (DEFAULT_SIZE - adjustedSize) / 2;
    const y = (DEFAULT_SIZE - adjustedSize) / 2;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexRow
            verticalAlignment='center'
            gap={SPACER_2}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
          >
            {this.labelPosition() === 'left' && (
              <Label wireframe={this} color={textColor} opacity={opacity} />
            )}
            <Rect
              borderRadius={DEFAULT_SIZE}
              width={DEFAULT_SIZE}
              height={DEFAULT_SIZE}
              fill={'white'}
              border={{
                color: fill,
                opacity,
                lineWidth: BORDER_WIDTH,
              }}
            >
              <Rect
                x={x}
                y={y}
                borderRadius={DEFAULT_SIZE}
                width={adjustedSize}
                height={adjustedSize}
                fill={{
                  color: filled ? fill : 'transparent',
                  opacity,
                }}
              />
            </Rect>
            {this.labelPosition() === 'right' && (
              <Label color={textColor} opacity={opacity} wireframe={this} />
            )}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeRadioButton;
